import { useEffect, useRef, useState } from 'react';

import { useEventsContext } from '@/providers/Events';

import useQuestionnaireFormContext from './QuestionnaireFormProvider/useQuestionnaireFormContext';
import useQuestionnaireContext from './useQuestionnaireContext';

export type State = 'editing' | 'saving' | 'waiting' | 'processing';

const MAX_PROCESSING_TIME = 1_000;
const MAX_EDITING_TIME = 2_000;

// Some components don't call onBlur when the user clicks away.
// Need to make sure that these components don't get us stuck in the editing state.
const trustOnBlur = (fieldType: string): boolean =>
  [
    'add-exemptions',
    // 'address-international',
    // 'address-us',
    // 'bankruptcy-statute-select',
    'break',
    // 'court-division-select',
    // 'creditor-select',
    'currency',
    'date',
    'explanation',
    'list',
    'long-text',
    'median-income',
    'monthly-income',
    'multi-select',
    'name',
    'number',
    'numeric',
    'percent',
    'phone-number',
    // 'selection',
    'short-text',
    'single-select',
    'ssn',
    'table',
  ].includes(fieldType);

const extractDirtyFieldKeys = (dirtyFields: Record<string, unknown>): string[] =>
  Object.keys(dirtyFields).filter((key) => !key.includes('__ExplanationHTML'));

type Props = {
  onChange: (state: State) => void;
};

const FormState: React.FC<Props> = ({ onChange }) => {
  const { questionnaireId } = useQuestionnaireContext();
  const { formMethods } = useQuestionnaireFormContext();
  const { addListener, emit, removeListener } = useEventsContext();

  const [currentState, setCurrentState] = useState<State>('waiting');

  const [isEditingField, setIsEditingField] = useState(false);
  const [editingFieldType, setEditingFieldType] = useState<string | null>(null);
  const [isSavingResponse, setIsSavingResponse] = useState(false);

  useEffect(() => {
    const listener = addListener('fieldEditingStateChanged', ({ isEditing, fieldType }) => {
      setIsEditingField(isEditing);
      setEditingFieldType(isEditing ? fieldType : null);
    });
    return () => {
      removeListener('fieldEditingStateChanged', listener);
    };
  }, [addListener, removeListener]);

  useEffect(() => {
    const listener = addListener('isResponseUpdating', ({ status }) => {
      setIsSavingResponse(status);
    });
    return () => {
      removeListener('isResponseUpdating', listener);
    };
  }, [addListener, removeListener]);

  const currentStateRef = useRef<State>(currentState);
  currentStateRef.current = currentState;
  // Finite state machine transitions
  // - saving has highest priority when isSavingResponse === true
  // - editing when isEditingField === true
  // - processing when there are dirty fields, but not saving.
  // - waiting is the base state
  useEffect(() => {
    // Priority: saving -> editing -> processing -> waiting
    if (isSavingResponse) {
      setCurrentState('saving');
      return;
    }

    if (isEditingField) {
      setCurrentState('editing');
      return;
    }

    const dirtyFieldKeys = extractDirtyFieldKeys(formMethods.formState.dirtyFields);
    if (dirtyFieldKeys.length > 0) {
      setCurrentState('processing');
      return;
    }

    setCurrentState('waiting');
  }, [isEditingField, isSavingResponse, formMethods]);

  // Don't let processing state persist for more than 1 second
  const processingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (currentState === 'processing') {
      if (processingTimerRef.current) {
        clearTimeout(processingTimerRef.current);
        processingTimerRef.current = null;
      }
      processingTimerRef.current = setTimeout(() => {
        if (currentState === 'processing') {
          setCurrentState('waiting');
        }
      }, MAX_PROCESSING_TIME);
    }
    return () => {
      if (processingTimerRef.current) {
        clearTimeout(processingTimerRef.current);
        processingTimerRef.current = null;
      }
    };
  }, [currentState]);

  // Don't let editing state persist for components with known onBlur problems.
  const editingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (currentState === 'editing' && editingFieldType && !trustOnBlur(editingFieldType)) {
      if (editingTimerRef.current) {
        clearTimeout(editingTimerRef.current);
        editingTimerRef.current = null;
      }
      editingTimerRef.current = setTimeout(() => {
        if (currentState === 'editing') {
          setCurrentState('waiting');
        }
      }, MAX_EDITING_TIME);
    }
    return () => {
      if (editingTimerRef.current) {
        clearTimeout(editingTimerRef.current);
        editingTimerRef.current = null;
      }
    };
  }, [currentState, editingFieldType]);

  useEffect(() => {
    emit('questionnaireStateChange', { questionnaireId, state: currentState });
    onChange(currentState);
  }, [currentState, emit, questionnaireId]);

  return null;
};

export default FormState;
